// Customer fill-in worksheet — a browser-generated .xlsx the engineer hands to the customer to close
// the design's open gaps. Built client-side from the loaded Design Model with SheetJS (already a
// dependency, used by extractText.js), mirroring the deterministic projector shape of the backend
// test-gen workbooks (lambda/solnstudio/test-gen/workbooks.js::apiTestDataSheet). No backend/job needed.
//
// Three sheets:
//   - Open Questions      — one row per model.openQuestions[] (incl. the auto provenance questions) + Answer
//   - Integration Data Needed — per integration.testDataNeeded[] field the customer must supply
//   - Additional Use Cases    — a blank intake form for the customer to propose new intents/use cases
//
// The completed file re-uploads through the normal Sources flow (xlsx already handled by extractText);
// the engineer tags it `design-input` so the next generation pass folds the answers in.

import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx'; // read-only: extractWorksheetAnswers parses re-uploaded workbooks

const BLANK_USE_CASE_ROWS = 12;
const BLANK_QUESTION_ROWS = 6; // spare rows for the engineer to add new open questions (leave # blank)

function openQuestionsAoA(model) {
  // Status + Answer columns let the sheet carry the full record: questions already answered in-app show
  // as Resolved with their answer prefilled, so the customer only fills the remaining Open rows. The
  // round-trip parser (extractWorksheetAnswers) locates the `#` and `Answer` columns by header name, so
  // the inserted Status column doesn't affect re-upload.
  const header = ['#', 'Category', 'Question', 'Status', 'Answer'];
  const rows = (model.openQuestions || []).map((q) => [
    q.num ?? '',
    q.category || '',
    q.question || '',
    q.status === 'resolved' ? 'Resolved' : 'Open',
    q.answer || '', // prefilled if answered in-app; otherwise blank for the customer to fill
  ]);
  if (rows.length === 0) rows.push(['', '', '(No open questions — the design is fully grounded.)', '', '']);
  // Blank rows so the engineer can ADD new questions: type a Question, leave # blank — on re-upload the
  // tool creates them as new open questions (extractWorksheetAnswers picks up #-less Question rows).
  for (let i = 0; i < BLANK_QUESTION_ROWS; i++) rows.push(['', '', '', '', '']);
  return [header, ...rows];
}

function integrationDataAoA(model) {
  const header = ['Integration', 'Field', 'Description', 'Test Data Needed (from Customer)', 'Answer'];
  const rows = [];
  for (const integ of model.integrations || []) {
    const needed = integ.testDataNeeded || [];
    if (needed.length === 0) {
      rows.push([integ.name || '', '(none specified)', integ.endpoint || '', '', '']);
      continue;
    }
    for (const field of needed) {
      const f = typeof field === 'string' ? { name: field } : field;
      rows.push([integ.name || '', f.name || '', f.description || '', f.example || 'TBD', '']);
    }
  }
  if (rows.length === 0) rows.push(['(No integrations identified yet.)', '', '', '', '']);
  return [header, ...rows];
}

function additionalUseCasesAoA() {
  const header = ['Use case', 'Caller goal', 'Example phrasing', 'Self-service? (Y/N)', 'Target queue', 'Notes'];
  const blanks = Array.from({ length: BLANK_USE_CASE_ROWS }, () => ['', '', '', '', '', '']);
  return [header, ...blanks];
}

// Per-sheet layout: the column widths + which columns wrap. Index-aligned with each sheet's header
// row. Without this the workbook ships with default narrow columns and no wrapping (the readability
// complaint); exceljs (already a dependency, used by dddWorksheet) gives us real formatting.
const SHEET_LAYOUT = {
  'Open Questions': { aoa: openQuestionsAoA, widths: [6, 18, 60, 12, 50], wrap: [2, 4] },
  'Integration Data Needed': { aoa: integrationDataAoA, widths: [24, 20, 42, 32, 32], wrap: [2, 3, 4] },
  'Additional Use Cases': { aoa: additionalUseCasesAoA, widths: [26, 32, 36, 16, 22, 32], wrap: [0, 1, 2, 4, 5] },
};

const HEADER_FILL = 'FF2F6FB0'; // a calm blue header band
const BORDER = { style: 'thin', color: { argb: 'FFD0D7DE' } };

function addStyledSheet(wb, name, model) {
  const { aoa, widths, wrap } = SHEET_LAYOUT[name];
  const ws = wb.addWorksheet(name, { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = widths.map((w) => ({ width: w }));
  const wrapSet = new Set(wrap);
  (typeof aoa === 'function' ? aoa(model) : aoa).forEach((rowArr, rowIdx) => {
    const row = ws.addRow(rowArr);
    if (rowIdx === 0) {
      row.height = 22;
      row.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        cell.border = { bottom: BORDER };
      });
    } else {
      widths.forEach((_w, ci) => {
        row.getCell(ci + 1).alignment = { vertical: 'top', wrapText: wrapSet.has(ci) };
      });
    }
  });
}

/** Build the styled customer workbook (exceljs) for a Design Model. */
function buildCustomerWorkbook(model) {
  const wb = new ExcelJS.Workbook();
  for (const name of Object.keys(SHEET_LAYOUT)) addStyledSheet(wb, name, model);
  return wb;
}

function customerFileName(model, projectName) {
  const safeName = (projectName || model.meta?.customer || 'Project')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '');
  return `${safeName}_Customer_Worksheet.xlsx`;
}

/** Build the customer worksheet into an ArrayBuffer (no download) — for persisting as an artifact. */
export async function buildCustomerArrayBuffer(designModel) {
  return buildCustomerWorkbook(designModel || {}).xlsx.writeBuffer();
}

/** Build + trigger a direct browser download of the customer worksheet. */
export async function buildCustomerWorksheet(designModel, { filename, projectName } = {}) {
  const model = designModel || {};
  const buffer = await buildCustomerWorkbook(model).xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || customerFileName(model, projectName);
  a.click();
  URL.revokeObjectURL(url);
}

const OPEN_QUESTIONS_SHEET = 'Open Questions';

/**
 * Detect, from a re-uploaded customer worksheet, both (a) filled-in answers to EXISTING questions
 * (matched back to their open-question `id` by the `#`/num column) and (b) NEW questions the engineer
 * typed in spare rows (a Question with a blank/unknown `#`). Returns empty lists for any workbook that
 * isn't a customer worksheet (no "Open Questions" sheet) — safe to call on every xlsx upload.
 *
 * @param {File} file               the uploaded .xlsx File
 * @param {Array} openQuestions     model.openQuestions (carry `num` + `id` + `question`)
 * @returns {Promise<{ answers: Array<{oqId,num,answer}>, added: Array<{question,category,answer}> }>}
 */
export async function extractWorksheetAnswers(file, openQuestions = []) {
  const empty = { answers: [], added: [] };
  if (!file) return empty;
  let wb;
  try {
    wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  } catch {
    return empty;
  }
  const sheet = wb.Sheets?.[OPEN_QUESTIONS_SHEET];
  if (!sheet) return empty;

  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
  if (!rows.length) return empty;
  // Header row defines column order; tolerate reordering by locating columns by name.
  const header = rows[0].map((h) => String(h || '').trim().toLowerCase());
  const numCol = header.indexOf('#');
  const questionCol = header.indexOf('question');
  const answerCol = header.indexOf('answer');
  if (numCol === -1 || answerCol === -1) return empty;

  const byNum = new Map(openQuestions.filter((q) => q.num != null).map((q) => [Number(q.num), q]));
  const existingText = new Set(openQuestions.map((q) => String(q.question || '').replace(/\s+/g, ' ').trim().toLowerCase()));
  const answers = [];
  const added = [];
  for (const row of rows.slice(1)) {
    const num = Number(row[numCol]);
    const answer = String(row[answerCol] ?? '').trim();
    const question = questionCol === -1 ? '' : String(row[questionCol] ?? '').replace(/\s+/g, ' ').trim();
    const q = Number.isNaN(num) ? null : byNum.get(num);

    if (q?.id) {
      // Existing question — apply the answer if it changed. (Prefilled resolved rows that are unchanged
      // are skipped so a re-upload doesn't needlessly re-resolve every prior answer.)
      if (!answer) continue;
      if (q.status === 'resolved' && String(q.answer ?? '').trim() === answer) continue;
      answers.push({ oqId: q.id, num, answer });
    } else if (question && !existingText.has(question.toLowerCase())) {
      // A new question the engineer added (blank/unknown #, not a duplicate of an existing one).
      added.push({ question, category: questionCol > 1 ? String(row[1] ?? '').trim() : '', answer });
      existingText.add(question.toLowerCase()); // guard against duplicate rows within the same upload
    }
  }
  return { answers, added };
}

export default buildCustomerWorksheet;
